import { getTagsName } from "@components/Elements/GAData/GADataHelper";
import {
  CompanionAdsLb,
  CompanionAdsLb1,
} from "@elements/Advertisement/variant/CompanionAdsLb";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@elements/LoadMore/CreateLoadMoreHandler";
import LoadMore from "@elements/LoadMore/LoadMore";
import Outbrain from "@elements/OutbrainAds/Outbrain";
import fetchBHApi from "@helper/fetchBHApi";
import { isArrayWithElements, isClientSide } from "@helper/utils";
import useVisibleArticle from "@hooks/useVisibleArticle";
import ArticleElementWrapper from "@pages/Article/Components/ArticleElementWrapper";
import UGCArticleContentCard from "@pages/UGC/UGCArticlePage/UGCArticleContentCard";
import { MATA_MATA_SECTION } from "@pages/UGC/UGCLandingPage/UGCLandingPage";
import AdsContainer from "@src/app/components/Container/AdsContainer";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { ArticleAdTargetKeys, PageAdTargetValue } from "@typings/Ads.d";
import {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import React, { useEffect, useState } from "react";

import UGCArticleContentCardNew from "./UGCArticleLayout/UGCArticleContentCardNew";

type UGCArticleInfiniteScrollProps = {
  mainArticle: ProcessedArticleData;
  latestRegisteredArticle: ProcessedArticleData[];
  pageAdTargetValue: PageAdTargetValue;
  onNewArticle: (
    article: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => void;
  isOutbrainEnabled: boolean;
  newDesign?: boolean;
};

export default function UGCArticleInfiniteScroll({
  mainArticle,
  latestRegisteredArticle,
  pageAdTargetValue,
  onNewArticle,
  isOutbrainEnabled = false,
  newDesign,
}: UGCArticleInfiniteScrollProps) {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>([mainArticle.id]),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [visibleArticleId, setVisibleArticleId] = useState<string>(
    mainArticle.id,
  );

  // Trigger useVisibleArticle when loadMoreData changes
  const visibleArticleIdFromHook = useVisibleArticle(visibleArticleId);
  const [currentPath, setCurrentPath] = useState<string>(
    mainArticle.urlPath || "",
  );

  const filterArticlesBasedOn = mainArticle.campaignReference?.id;

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? mainArticle : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn,
      excludeIds: [...renderedArticleIds].join(","),
    };

    const fetchNextCampaignStoryData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[]> => {
      const queryParams = new URLSearchParams({
        campaignReferenceId: requestData.filterArticlesBasedOn as string,
        excludeIds: requestData.excludeIds as string,
      }).toString();
      const response: ProcessedArticleData[] = await fetchBHApi(
        `get-campaign-article?${queryParams}`,
        "GET",
      );

      return response;
    };

    const fetchFunction: FetchMoreArticlesFunction = fetchNextCampaignStoryData;
    const updateGADataForArticle = true;
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: 1,
      pageNumber,
      updateGADataForArticle,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);

    if (isArrayWithElements(response)) {
      const article = response[0];
      setVisibleArticleId(article.id);
      setCurrentPath(article.urlPath || "");
      onNewArticle(article, pageNumber + 1, false);
    } else {
      setHasMoreStories(false);
    }

    return response;
  };

  useEffect(() => {
    if (!isClientSide || !visibleArticleId) return;

    const allArticles = [mainArticle, ...loadMoreData];
    const visibleArticleIndex = allArticles.findIndex(
      (article) => article.id === visibleArticleId,
    );

    const articleData =
      visibleArticleIndex !== -1 ? allArticles[visibleArticleIndex] : undefined;

    if (articleData) {
      const newPath = articleData.urlPath;
      if (newPath && newPath !== currentPath) {
        window.history.pushState("article", articleData.title, newPath);
        setCurrentPath(newPath || "");
        onNewArticle(articleData, visibleArticleIndex, true);
      }
    }
  }, [visibleArticleId, loadMoreData, mainArticle, onNewArticle, currentPath]);

  useEffect(() => {
    if (!isClientSide) return;
    if (visibleArticleIdFromHook) {
      setVisibleArticleId(visibleArticleIdFromHook);
    }
  }, [visibleArticleIdFromHook]);

  return (
    <div>
      {[...loadMoreData].map((context, index) => (
        <React.Fragment key={context.id}>
          {newDesign ? (
            <AdsContainer className="my-6 lg:my-8">
              <CompanionAdsLb1
                uniqueSectionName={MATA_MATA_SECTION}
                stripeBackground={false}
                index={index + 1}
                pageAdTargetType={pageAdTargetValue}
                gsChannels={context.gsChannels}
                articleSlotTarget={[
                  { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                  {
                    key: ArticleAdTargetKeys.TAGS,
                    value: getTagsName(context.tags || []).toLowerCase(),
                  },
                ]}
              />
            </AdsContainer>
          ) : (
            <CompanionAdsLb
              uniqueSectionName={MATA_MATA_SECTION}
              index={index + 1}
              pageAdTargetType={pageAdTargetValue}
              gsChannels={context.gsChannels}
              articleSlotTarget={[
                { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                {
                  key: ArticleAdTargetKeys.TAGS,
                  value: getTagsName(context.tags || []).toLowerCase(),
                },
              ]}
            />
          )}
          <div id={context.id} className="article-card">
            {newDesign ? (
              <>
                <UGCArticleContentCardNew
                  key={index}
                  mainArticle={context}
                  latestRegisteredArticle={latestRegisteredArticle}
                  pageAdTargetValue={pageAdTargetValue}
                  pageNumber={index + 1}
                  isInfiniteScrollArticle={true}
                  gsChannels={context.gsChannels}
                  articleSlotTarget={[
                    { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                    {
                      key: ArticleAdTargetKeys.TAGS,
                      value: getTagsName(context.tags || []).toLowerCase(),
                    },
                  ]}
                  index={index}
                />
                <ArticleElementWrapper>
                  {isOutbrainEnabled && context.urlPath && (
                    <div className="mt-4 lg:mt-6 border-t-2 border-blue-400">
                      <Outbrain pathName={context.urlPath} />
                    </div>
                  )}
                </ArticleElementWrapper>
              </>
            ) : (
              <UGCArticleContentCard
                key={index}
                mainArticle={context}
                latestRegisteredArticle={latestRegisteredArticle}
                pageAdTargetValue={pageAdTargetValue}
                pageNumber={index + 1}
                isInfiniteScrollArticle={true}
                isOutbrainEnabled={isOutbrainEnabled}
                gsChannels={context.gsChannels}
                articleSlotTarget={[
                  { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                  {
                    key: ArticleAdTargetKeys.TAGS,
                    value: getTagsName(context.tags || []).toLowerCase(),
                  },
                ]}
                index={index}
              />
            )}
          </div>
        </React.Fragment>
      ))}
      <ArticleElementWrapper className="mt-12 mb-6 lg:mt-20 lg:mb-10">
        <LoadMore
          rootClassName="my-4"
          onLoadMore={handleLoadMore}
          hasMore={hasMoreStories}
          loadText="Lagi Cerita"
          isArticlePage={true}
          virtualPVTitle={mainArticle.title}
          pageNumber={pageNumber}
        />
      </ArticleElementWrapper>
    </div>
  );
}
